import { ClippingBoxViewControls } from "@/components/ui/clipping-box-view-controls";

import { selectIpxEstimatedRegionBytes } from "@/store/modes/ipx-order-mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveWizardStepKey } from "@/store/wizard/wizard-selectors";
import { useBoxControlsContext } from "@/utils/box-controls-context";
import { useBreakpointMdUp, useToast } from "@faro-lotv/flat-ui";
import { useEffect, type JSX } from "react";
import { IpxCheckoutDialog } from "./ipx-checkout-dialog";
import { IpxOrderStepKey } from "./ipx-order-steps";
import { recenterIpxViewEvent } from "./ipx-view-events";

/** @returns the in-scene tools of the active step, and the IPX checkout once the user continues to it */
export function IpxOrderModeOverlay(): JSX.Element {
  return (
    <>
      <AreaTools />
      <PointCloudLoadingToast />
      <IpxCheckoutDialog />
    </>
  );
}

/** @returns nothing, shows a toast while the selected point cloud is loading */
function PointCloudLoadingToast(): null {
  const isLoading = useAppSelector(
    (state) =>
      !!state.ipxOrderMode.pointCloudId &&
      selectIpxEstimatedRegionBytes(state) === undefined,
  );
  const { openToast, closeToast } = useToast();

  useEffect(() => {
    if (!isLoading) return;
    const key = openToast({
      title: "Loading the point cloud…",
      variant: "info",
      persist: true,
    });
    return () => closeToast(key);
  }, [closeToast, isLoading, openToast]);

  return null;
}

/** @returns the in-scene tools of the data and review steps: reset and fit the box while editing it, recenter and switch the camera */
function AreaTools(): JSX.Element | null {
  const stepKey = useAppSelector(selectActiveWizardStepKey);
  const isMdUp = useBreakpointMdUp();
  const { autoBoxEvent, resetBoxEvent } = useBoxControlsContext();

  const isEditingBox = stepKey === IpxOrderStepKey.defineArea;
  if (!isEditingBox && stepKey !== IpxOrderStepKey.review) return null;

  return (
    <ClippingBoxViewControls
      onResetBox={isEditingBox ? () => resetBoxEvent.emit() : undefined}
      // The box handles can't be dragged on touch screens, there the box is fitted to the view instead
      onFitBoxToView={isEditingBox ? () => autoBoxEvent.emit() : undefined}
      onRecenterView={() => recenterIpxViewEvent.emit()}
      // Small screens keep only the essential tools, to fit above the step sheet
      showCameraSwitch={isMdUp}
    />
  );
}
